import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { Switch } from '@headlessui/react';
import { useEquipmentTypes } from '../../context/EquipmentTypeContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
  
const schema = yup.object().shape({
  name: yup.string().required('Type name is required'),
  parent_category_id: yup.string().nullable(),
  image: yup.mixed()
    .nullable()
    .test('fileSize', 'File size is too large', value => {
      if (!value) return true;
      return value.size <= 5000000; // 5MB
    })
    .test('fileType', 'Unsupported file type', value => {
      if (!value) return true;
      return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
    }),
  active: yup.boolean().default(true)
});

const EquipmentTypeForm = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addEquipmentType, updateEquipmentType, getEquipmentTypeById, getEquipmentTypes } = useEquipmentTypes();
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/equipment-types');
  
  const ranOnceRef2 = useRef(false);
  const { showLoading, hideLoading } = useLoading();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setError,
    
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if(!id){
      reset({
        name:'',
        parent_category_id:'',
        active:true
      }); 
      setImagePreview(null)
    }
  }, [id,reset]);

  
  // Load categories data
  useEffect(() => {
    const loadData = async () => {
      try {
        showLoading()
        // Load all categories for parent dropdown
         
        if (id) {
         
          const categoryResponse = await getEquipmentTypeById(id);
          setIsLoading(true)
        
          if (categoryResponse.status === 200) {
            let  category = categoryResponse.data;
            
            const formData = {
              name: category.name,
              parent_category_id: category.parent_category_id || '',
              active: category.active
            };
            setIsActive(category.active);
            if (category.display_image?.path) {
              setImagePreview(category.display_image?.path);
            }
            reset(formData);
          } else {
            toast.error('Type not found');
            navigate(getRoutePath('/equipment-types'));
          }
          hideLoading()
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        hideLoading()
        console.error('Error loading data:', error);
        toast.error('Error loading type data');
      } finally {
        setIsLoading(false)
        hideLoading()
        
      }
    };
    if (ranOnceRef2 && !ranOnceRef2.current) {
      loadData();
      ranOnceRef2.current = true;
    }
  }, [id, reset, navigate, getEquipmentTypeById, getRoutePath, initialized,getEquipmentTypes,hideLoading,showLoading,ranOnceRef2]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading()
    try {
      const formData = {
        ...data,
        image: fileInputRef.current?.files[0] || null,
        active: isActive
      };
      
      let response;
      if (id) {
        // Update existing type
        response = await updateEquipmentType(id, formData);
      } else {
        // Create new type
        response = await addEquipmentType(formData);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Type updated successfully!' : 'Type created successfully!');
        navigate(getRoutePath('/equipment-types'));
      } else if (response.status === 422) {
        // Handle validation errors
        Object.keys(response.errors).forEach(field => {
          setError(field, { message: response.errors[field][0] });
          toast.error(response.errors[field][0]);
        });
      } else {
        toast.error('Error saving type');
      }
      hideLoading()
    } catch (error) {
      toast.error('Error saving type');
      console.error('Error:', error);
      hideLoading()
    } finally {
      hideLoading()
      setIsLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setValue('image', null);
    setValue('remove_image',1)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'Edit Equipment Type' : 'Create New Equipment Type'}
      </h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Type Name</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter type name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
 

          <div>
            <label className="block text-sm font-medium text-gray-700">Image (max 180 X 140 px)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-2 block w-full text-sm text-gray-500
 file:mr-4 file:py-2 file:px-4
 file:rounded-md file:border-0
 file:text-sm file:font-semibold
 file:bg-blue-50 file:text-blue-700
 hover:file:bg-blue-100"
            />
            {imagePreview && (
              <div className="mt-2 relative inline-block">
                <img src={imagePreview} alt="Preview" className="h-32 w-32 object-cover rounded" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {errors.image && (
              <p className="mt-1 text-sm text-red-600">{errors.image.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activation Status</label>
            <Switch
              checked={isActive}
              onChange={(value) => {
                setIsActive(value);
                setValue('active', value);
              }}
              className={`${
                isActive ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              {isLoading ? 'Saving...' : (id ? 'Update Type' : 'Create Type')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentTypeForm; 